/**
 * Sidebar Component
 *
 * Contains summary ribbon, watch zone management,
 * and tabbed view for event list/timeline.
 */

import { useState, lazy, Suspense } from 'react';
import { AreaPreset, UnifiedEvent } from '../types/events';
import EventList from './EventList';
import SummaryRibbon from './SummaryRibbon';
import SupporterBadge from './SupporterBadge';
import { SkeletonFireCardList } from './Skeleton';
import { WatchZone } from '../api/client';

// EventTimeline (927 lines) only renders when the user clicks the Timeline
// tab — most users stay on the list view. Lazy-load it to keep the initial
// bundle small.
const EventTimeline = lazy(() => import('./EventTimeline'));

// WatchZoneManager (938 lines) only renders for signed-in users who open
// the watch zone section. Lazy-load to defer the cost.
const WatchZoneManager = lazy(() => import('./WatchZoneManager'));

type ViewTab = 'list' | 'timeline';

interface SidebarProps {
  events: UnifiedEvent[];
  selectedArea: AreaPreset;
  isLoading: boolean;
  error: string | null;
  onAreaChange?: (area: AreaPreset) => void; // Kept for compatibility, no longer used in UI
  onEventSelect: (event: UnifiedEvent) => void;
  // Watch zone management props
  onLoginClick?: () => void;
  onWatchZoneSelect?: (zone: WatchZone) => void;
  // Map integration for watch zone editor
  onEditorClose?: () => void;
  onEditorCenterChange?: (center: { lat: number; lon: number }) => void;
  onEditorRadiusChange?: (radiusKm: number) => void;
  // Polygon drawing integration
  onShapeTypeChange?: (shapeType: 'circle' | 'polygon') => void;
  onDrawingStart?: () => void;
  // Zone visibility on map
  visibleZoneIds?: Set<string>;
  onToggleZoneVisibility?: (zoneId: string) => void;
  onZoneDeleted?: (zoneId: string) => void;
}

export default function Sidebar({
  events,
  selectedArea,
  isLoading,
  error,
  onAreaChange: _onAreaChange,
  onEventSelect,
  onLoginClick,
  onWatchZoneSelect,
  onEditorClose,
  onEditorCenterChange,
  onEditorRadiusChange,
  onShapeTypeChange,
  onDrawingStart,
  visibleZoneIds,
  onToggleZoneVisibility,
  onZoneDeleted,
}: SidebarProps) {
  const [isWatchZonesOpen, setIsWatchZonesOpen] = useState(true); // Default open for visibility
  const [activeTab, setActiveTab] = useState<ViewTab>('timeline');

  return (
    <aside className="sidebar">
      {/* Summary Ribbon - fixed at top */}
      <SummaryRibbon
        events={events}
        locationName={selectedArea.name}
      />

      {/* Scrollable content area */}
      <div className="sidebar-scroll-content">
        {/* Watch Zones Panel - Persistent zones with notifications */}
        <div className="sidebar-section">
          <button
            className="sidebar-section-header"
            onClick={() => setIsWatchZonesOpen(!isWatchZonesOpen)}
            aria-expanded={isWatchZonesOpen}
          >
            <span>My Watch Zones</span>
            <span className="sidebar-toggle">{isWatchZonesOpen ? '−' : '+'}</span>
          </button>

          {isWatchZonesOpen && (
            <div className="sidebar-section-content">
              <Suspense fallback={null}>
                <WatchZoneManager
                  onZoneSelect={onWatchZoneSelect}
                  onLoginClick={onLoginClick || (() => {})}
                  onEditorClose={onEditorClose}
                  onCenterChange={onEditorCenterChange}
                  onRadiusChange={onEditorRadiusChange}
                  onShapeTypeChange={onShapeTypeChange}
                  onDrawingStart={onDrawingStart}
                  visibleZoneIds={visibleZoneIds || new Set()}
                  onToggleZoneVisibility={onToggleZoneVisibility || (() => {})}
                  onZoneDeleted={onZoneDeleted}
                />
              </Suspense>
            </div>
          )}
        </div>

        {/* Status. The loading state is now handled inline by skeleton
            placeholders in the events section below — only error banner
            remains here. */}
        {error && (
          <div className="sidebar-status error">
            {error}
          </div>
        )}

        {/* View Tabs */}
        <div className="view-tabs">
          <button
            className={`view-tab ${activeTab === 'list' ? 'view-tab-active' : ''}`}
            onClick={() => setActiveTab('list')}
          >
            List
          </button>
          <button
            className={`view-tab ${activeTab === 'timeline' ? 'view-tab-active' : ''}`}
            onClick={() => setActiveTab('timeline')}
          >
            Timeline
          </button>
        </div>

        {/* Events Content. Skeleton placeholders show while the first
            fetch is in flight — better than a blank panel for users on
            slow connections. */}
        <div className="sidebar-section events-section">
          <div className="sidebar-section-content events-list-container">
            {isLoading && events.length === 0 ? (
              <SkeletonFireCardList count={6} />
            ) : activeTab === 'list' ? (
              <EventList events={events} onEventClick={onEventSelect} />
            ) : (
              <Suspense fallback={null}>
                <EventTimeline
                  events={events}
                  onEventClick={onEventSelect}
                />
              </Suspense>
            )}
          </div>
        </div>
      </div>

      {/* Supporter Badge - fixed at bottom */}
      <div className="sidebar-footer">
        <SupporterBadge />
      </div>
    </aside>
  );
}

